import { LitElement, PropertyValues, html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";

/**
 * Searchable select web component that can be used for single or multiple selection
 * @attr {string} name - Input name
 * @attr {string} multiple-selected-content-label - Label for the selected options count
 * @attr {string} placeholder - Placeholder text for the trigger
 * @attr {string} search-placeholder - Placeholder text for the search input
 * @attr {boolean} multiple - Whether multiple options can be selected
 * @attr {string} options - JSON string of options [{value: string, label: string}]
 * @attr {string} selected - JSON string of selected values (array for multiple, string for single)
 * @attr {string} form-selector - Form selector
 * @attr {string} no-results-label - Label for the no results message
 * @attr {boolean} hide-search - Whether to hide the search input
 * @attr {boolean} disabled - Whether the select is disabled
 * @attr {string} disabled-message - Message to display when the select is disabled
 * @attr {boolean} custom-checkbox - Whether to use a custom checkbox
 * @attr {string} custom-checkbox-class - Class for the custom checkbox
 * @attr {boolean} enable-empty-option - Whether to enable the empty option
 * @attr {string} empty-option-label - Label for the empty option
 */
@customElement("searchable-select")
export class SearchableSelect extends LitElement {
  @property({ type: String })
  name = "";

  @property({ type: String, attribute: "multiple-selected-content-label" })
  multipleSelectedContentLabel = "Opções selecionadas";

  @property({ type: String })
  placeholder = "Selecione a opção";

  @property({ type: String, attribute: "content-title" })
  contentTitle = "Selecionar Opção";

  @property({ type: String, attribute: "search-placeholder" })
  searchPlaceholder = "Buscar opção";

  @property({ type: Boolean })
  multiple = false;

  @property({ type: String })
  options = "[]";

  @property({ type: String })
  selected = "";

  @property({ type: String })
  formSelector = "";

  @property({ type: String, attribute: "no-results-label" })
  noResultsLabel = "Sem resultados";

  @property({ type: Boolean, attribute: "hide-search" })
  hideSearch = false;

  @property({ type: Boolean, reflect: true })
  disabled = false;

  @property({ type: String, attribute: "disabled-message" })
  disabledMessage = "Selecione uma opção";

  @property({ type: Boolean, attribute: "custom-checkbox" })
  customCheckbox = false;

  @property({ type: String, attribute: "custom-checkbox-class" })
  customCheckboxClass = "custom-checkbox";

  @property({ type: Boolean, attribute: "enable-empty-option" })
  enableEmptyOption = false;

  @property({ type: String, attribute: "empty-option-label" })
  emptyOptionLabel = "Selecione uma opção";

  @state() private isOpen = false;
  @state() private searchTerm = "";
  @state() private parsedOptions: Array<{ value: string; label: string }> = [];
  @state() private parsedSelected: string[] = [];
  @state() private isMobile = window.innerWidth < 992;

  constructor() {
    super();
    this.handleWindowResize = this.handleWindowResize.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.handleFormReset = this.handleFormReset.bind(this);
    this.handleDismissOnKeydown = this.handleDismissOnKeydown.bind(this);
  }

  createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("resize", this.handleWindowResize);
    document.addEventListener("click", this.handleClickOutside);
    this.listenFormReset();
    this.parseOptions();
    this.parseSelected();
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.handleWindowResize);
    document.removeEventListener("click", this.handleClickOutside);
    this.removeFormResetListener();
    super.disconnectedCallback();
  }

  firstUpdated(_changedProperties: PropertyValues): void {
    this.toggleAttribute("initialized", true);
    this.dispatchEvent(
      new CustomEvent("initialized", {
        detail: {
          name: this.name,
          value: this.multiple
            ? this.parsedSelected
            : (this.parsedSelected[0] !== undefined ? this.parsedSelected[0] : ""),
        },
      })
    );
  }

  willUpdate(_changedProperties: PropertyValues): void {
    if (
      _changedProperties.has("disabled") &&
      _changedProperties.get("disabled")
    ) {
      this.reset();
    }

    if (_changedProperties.has("options") && this.options && this.hasUpdated) {
      this.parseOptions();
    }
  }

  public reset() {
    this.handleResetInputs();
    this.handleReset();
  }

  private get shouldRenderAsModalDialog() {
    return this.isMobile && !this.hideSearch;
  }

  private sendChangeEvent(reset = false) {
    this.dispatchEvent(
      new CustomEvent("change", {
        detail: {
          reset,
          name: this.name,
          value: this.multiple
            ? this.parsedSelected
            : (this.parsedSelected[0] !== undefined ? this.parsedSelected[0] : ""),
        },
      })
    );
  }

  private handleReset() {
    this.closeDropdown();
    this.searchTerm = "";
    this.parsedSelected = [];
    this.sendChangeEvent(true);
  }

  private handleResetInputs() {
    const inputs =
      this.querySelectorAll('input[type="checkbox"], input[type="radio"]') ??
      [];
    inputs.forEach((input: Element) => {
      (input as HTMLInputElement).checked = false;
    });
  }

  private handleFormReset() {
    this.handleReset();
  }

  private handleResetButtonClick(e: Event) {
    e.preventDefault();
    e.stopPropagation();
    this.handleResetInputs();
    this.handleReset();
  }

  private handleOptionLabelKeydown(e: KeyboardEvent) {
    const target = e.target as HTMLElement;
    if (!target) return;
    const input = document.getElementById(target.getAttribute("for") as string);
    if (input) {
      if (e.code === "Enter" || e.code === "Space") {
        (input as HTMLInputElement).click();
      }
    }
  }

  private listenFormReset() {
    let form = this.formSelector
      ? this.closest(this.formSelector)
      : this.closest("form");
    if (form) {
      form.addEventListener("reset", this.handleFormReset);
    }
  }

  private removeFormResetListener() {
    let form = this.formSelector
      ? this.closest(this.formSelector)
      : this.closest("form");
    if (form) {
      form.removeEventListener("reset", this.handleFormReset);
    }
  }

  private handleWindowResize() {
    this.isMobile = window.innerWidth < 1024;
  }

  private handleClickOutside(event: MouseEvent) {
    if (!this.isOpen) return;
    if (!this.contains(event.target as Node)) {
      this.closeDropdown();
    }
  }

  private handleDismissOnKeydown(e: KeyboardEvent) {
    if (this.isOpen && e.key === "Escape") {
      this.closeDropdown();
    }
  }

  private parseOptions() {
    try {
      this.parsedOptions = JSON.parse(this.options);
    } catch (e) {
      console.error("Invalid options format", e);
      this.parsedOptions = [];
    }
  }

  private parseSelected() {
    try {
      if (this.multiple) {
        this.parsedSelected = this.selected ? JSON.parse(this.selected) : [];
      } else {
        this.parsedSelected = this.selected ? [this.selected] : [];
      }
      this.parsedSelected = this.parsedSelected.filter((value) =>
        this.parsedOptions.some((option) => option.value === value)
      );
    } catch (e) {
      console.error("Invalid selected format", e);
      this.parsedSelected = [];
    }
  }

  private toggleDropdown() {
    if (this.disabled) return;

    if (this.shouldRenderAsModalDialog) {
      const dialog = this.querySelector("dialog");
      if (dialog) {
        if (this.isOpen) {
          (dialog as any).close();
        } else {
          (dialog as any).showModal();
        }
      }
    }

    this.isOpen = !this.isOpen;

    if (this.isOpen) {
      document.addEventListener("keydown", this.handleDismissOnKeydown);
    } else {
      document.removeEventListener("keydown", this.handleDismissOnKeydown);
    }

    if (!this.hideSearch) {
      const searchInput = this.querySelector("input.search-input");
      if (searchInput) {
        setTimeout(() => {
          (searchInput as HTMLInputElement).focus();
        }, 100);
      }
    }
  }

  private closeDropdown() {
    if (this.isMobile) {
      const dialog = this.querySelector("dialog");
      if (dialog) {
        (dialog as any).close();
      }
    }
    this.isOpen = false;
  }

  private handleSearch(e: InputEvent) {
    this.searchTerm = (e.target as HTMLInputElement).value;
  }

  private toggleOption(value: string) {
    if (this.multiple) {
      const index = this.parsedSelected.indexOf(value);
      if (index === -1) {
        this.parsedSelected = [...this.parsedSelected, value];
      } else {
        this.parsedSelected = this.parsedSelected.filter((v) => v !== value);
      }
    } else {
      this.handleResetInputs();
      this.parsedSelected = value ? [value] : [];
      this.closeDropdown();
    }

    // Dispatch change event
    this.sendChangeEvent();

    // Update the selected property
    this.selected = this.multiple
      ? JSON.stringify(this.parsedSelected)
      : JSON.stringify(this.parsedSelected[0] !== undefined ? this.parsedSelected[0] : "");
  }

  private getSelectedOptions() {
    return this.parsedOptions.filter((option) =>
      this.parsedSelected.includes(option.value)
    );
  }

  private getSelectedLabels() {
    if (this.parsedSelected.length === 0) {
      return this.placeholder;
    }

    const selectedOptions = this.getSelectedOptions();

    if (selectedOptions.length === 1) {
      return selectedOptions[0].label;
    }

    return html`${this.renderSelectedOptionsCount()}
    ${this.multipleSelectedContentLabel}`;
  }

  private getFilteredOptions() {
    if (!this.searchTerm) {
      return this.parsedOptions;
    }

    return this.parsedOptions.filter((option) =>
      option.label.toLowerCase().includes(this.searchTerm.toLowerCase())
    );
  }

  private renderSelectedOptionsCount() {
    const selectedOptions = this.getSelectedOptions();
    if (
      (!this.multiple && !this.shouldRenderAsModalDialog) ||
      selectedOptions.length < 1
    )
      return nothing;
    return html`<span class="selected-options-count"
      >${selectedOptions.length}</span
    >`;
  }

  private renderSelectTriggerClearButton() {
    if (this.parsedSelected.length > 0) {
      return html`<button
        class="clear-button"
        @click=${this.handleResetButtonClick}
      ></button>`;
    }

    return nothing;
  }

  private renderOptionItem(option: {
    value: string;
    label: string;
    isSelected: boolean;
  }) {
    return html`
      <label
        class="${classMap({
          "option-item": true,
          "option-item--selected": option.isSelected,
        })}"
        tabindex="0"
        for="${this.name}-${option.value}"
        @keydown=${this.handleOptionLabelKeydown}
        @click=${() => this.toggleOption(option.value)}
      >
        <input
          id="${this.name}-${option.value}"
          type="${this.multiple ? "checkbox" : "radio"}"
          value="${option.value}"
          ?checked=${option.isSelected}
          @change=${() => this.toggleOption(option.value)}
          class="${classMap({ hidden: this.customCheckbox })}"
        />
        ${this.customCheckbox
          ? html`<span class="${this.customCheckboxClass}"></span>`
          : nothing}
        ${option.label}
      </label>
    `;
  }

  private renderOptionsList() {
    const filteredOptions = this.getFilteredOptions();
    const selectedOptions = this.getSelectedOptions();

    const isSearchingEmptyOption =
      this.enableEmptyOption &&
      !this.multiple &&
      selectedOptions.length > 0 &&
      (!this.searchTerm ||
        (this.searchTerm &&
          this.emptyOptionLabel
            .toLowerCase()
            .includes(this.searchTerm.toLowerCase())));

    return html`
      <div class="options-list">
        ${isSearchingEmptyOption
          ? this.renderOptionItem({
              value: "",
              label: this.emptyOptionLabel,
              isSelected: selectedOptions.length === 0,
            })
          : nothing}
        ${filteredOptions.map((option) => {
          const isSelected = selectedOptions.some(
            (selectedOption) => selectedOption.value === option.value
          );
          return this.renderOptionItem({
            value: option.value,
            label: option.label,
            isSelected,
          });
        })}
        ${filteredOptions.length === 0 && !isSearchingEmptyOption
          ? html`<div class="no-results">${this.noResultsLabel}</div> `
          : nothing}
      </div>
    `;
  }

  private renderSearchInput() {
    if (this.hideSearch) {
      return nothing;
    }

    return html`
      <div class="search-input-container">
        <input
          type="text"
          class="search-input"
          placeholder="${this.searchPlaceholder}"
          @input=${this.handleSearch}
          .value=${this.searchTerm}
        />
      </div>
    `;
  }

  private renderDesktopDropdown() {
    return html`
      <div
        class="${classMap({
          "searchable-select-dropdown": true,
          "searchable-select-dropdown--open": this.isOpen,
        })}"
      >
        ${this.renderSearchInput()} ${this.renderOptionsList()}
      </div>
    `;
  }

  private renderMobileDialog() {
    const selectedOptions = this.getSelectedOptions();
    return html`
      <dialog
        class="${classMap({
          "searchable-select-dialog": true,
          "searchable-select-dialog--open": this.isOpen,
        })}"
      >
        <div class="dialog-header">
          <span class="dialog-header-title">${this.contentTitle}</span>
          <div class="dialog-header-actions">
            <button
              class="${classMap({
                "reset-button": true,
                "reset-button--hidden": selectedOptions.length === 0,
              })}"
              @click=${this.handleResetButtonClick}
              type="button"
            >
              ${this.renderSelectedOptionsCount()}Limpar
            </button>
            <button
              class="close-button"
              @click=${this.closeDropdown}
              type="button"
            ></button>
          </div>
        </div>
        ${this.renderSearchInput()} ${this.renderOptionsList()}
        ${this.multiple
          ? html`
              <div class="dialog-footer">
                <button
                  class="confirm-button"
                  @click=${this.closeDropdown}
                  type="button"
                >
                  Confirmar seleção
                </button>
              </div>
            `
          : nothing}
      </dialog>
    `;
  }

  private renderFormInput() {
    const selectedOptions = this.getSelectedOptions();
    if (selectedOptions.length === 0) return nothing;

    let value = "";
    if (!this.multiple || selectedOptions.length === 1) {
      value = selectedOptions[0].value;
    } else {
      value = selectedOptions.map((option) => option.value).join(",");
    }

    return html` <input type="hidden" name="${this.name}" value="${value}" /> `;
  }

  render() {
    return html`
      <div class="searchable-select" ?disabled=${this.disabled}>
        <button
          class="${classMap({
            "searchable-select-trigger": true,
            "searchable-select-trigger--open": this.isOpen,
            "searchable-select-trigger--disabled": this.disabled,
          })}"
          @click=${this.toggleDropdown}
          title=${this.disabled ? this.disabledMessage : ""}
          ?disabled=${this.disabled}
          type="button"
        >
          <span class="selected-option">${this.getSelectedLabels()}</span>
          <div class="searchable-select-trigger-actions">
            ${this.renderSelectTriggerClearButton()}
            <span class="dropdown-icon"></span>
          </div>
        </button>

        ${this.shouldRenderAsModalDialog
          ? this.renderMobileDialog()
          : this.renderDesktopDropdown()}
        ${this.renderFormInput()}
      </div>
    `;
  }
}
